import queryString from 'query-string'
import {set} from '@cred/shared/ga'
import {isClient} from '@cred/shared/utils'
import rollbar from '@cred/shared/rollbar'
import {
  isEligibleForDevice,
  isEligibleForOrganicTraffic,
  isEligibleForUTMTraffic,
  isExperimentEnabled
} from './utils'
import {USER_EXPERIMENTS} from './queries'
import {SAVE_USER_EXPERIMENTS} from './mutations'

const DISABLED_PARAM = 'vt_disabled',
  ONE_YEAR = 365 * 864e+5,
  getDefaultCookieOptions = () => ({
    expires: new Date(Date.now() + ONE_YEAR),
    path: '/',
    httpOnly: false,
    secure: true
  })

class ExperimentService {
  constructor({client, cookies, getCookieOptions, config, url, queryParams, userAgent, isAuthenticated}) {
    this.client = client
    this.cookies = cookies
    this.getCookieOptions = getCookieOptions || getDefaultCookieOptions
    this.config = config
    this.url = url
    this.queryParams = queryParams
    this.userAgent = userAgent
    this.isAuthenticated = isAuthenticated
    this.variationMap = {}
    this.forcedVariationMap = {}
    this.persistedVariationMap = {}
    this.cookiesVariationMap = {}
    this.defaultVariationMap = this.getDefaultVariationMap()
  }

  activateExperimentsById(idsList = []) {
    // Do not activate any experiment if the system is disabled
    this.updateDisabledFlag()

    if (this.isDisabled()) {
      return false
    }

    // Filter experiments by audience
    const experimentIds = this.filterByAudience(idsList)

    // Get variation map from cookies
    this.cookiesVariationMap = this.getCookiesVariationMap(experimentIds)
    Object.assign(this.variationMap, this.cookiesVariationMap)

    // Overwrite the forced variations in url
    this.forcedVariationMap = this.getForcedVariationMap()
    Object.assign(this.variationMap, this.forcedVariationMap)

    // Identify the experiments that requires the user to be bucket into a variation
    const newExperiments = experimentIds.filter(id => this.variationMap[id] === undefined)

    // Add new experiments variation to variation map
    Object.assign(this.variationMap, this.assignUserToVariation(newExperiments))

    this.setGaCustomDimensions(Object.keys(this.variationMap))

    return this.variationMap
  }

  /**
   * Activate all experiments for the given url
   */
  activateUrlExperiments() {
    return this.activateExperimentsById(this.getUrlExperimentsIds())
  }

  /**
   * Retun a variation map with new variation assignments
   */
  assignUserToVariation(experimentIds) {
    const variationMap = {}

    experimentIds.forEach(experimentId => {
      const experiment = this.getExperimenentConfig(experimentId)

      variationMap[experimentId] = this.getWeightedRandomVariation(experiment)
    })

    return variationMap
  }

  /**
   * Retun a sub set of the experimentIds that falls into experiment audience setup
   */
  filterByAudience(experimentIds) {
    return experimentIds.filter(experimentId => {
      const experiment = this.getExperimenentConfig(experimentId),
        eligibleForDevice = isEligibleForDevice(experiment, this.userAgent),
        eligibleForOrganic = isEligibleForOrganicTraffic(experiment, this.cookies),
        eligibleForOnlyUTM = isEligibleForUTMTraffic(experiment, this.cookies),
        experimentEnabled = isExperimentEnabled(experiment)

      return experimentEnabled && eligibleForDevice && eligibleForOrganic && eligibleForOnlyUTM
    })
  }

  /**
   * Check the experiment variation weights and randomlly return the index of one variation
   */
  getWeightedRandomVariation(experiment) {
    const variations = [...experiment.variations],
      randomNum = Math.random()

    let sum = 0

    for (let index in variations) {
      sum += variations[index].weight

      if (sum >= randomNum) {
        return variations[index].id
      }
    }
  }

  /**
   * Get the variation map from cookies
   */
  getCookiesVariationMap(idsList) {
    const variationMap = {}

    this.config.forEach(experiment => {
      if (isExperimentEnabled(experiment) && idsList.includes(experiment.id)) {
        const variation = this.cookies.get(`vt_${experiment.id}`, {path: '/'})

        if (variation) {
          variationMap[experiment.id] = parseInt(variation)
        }
      }
    })

    return variationMap
  }

  /**
   * Update cookies with the new variation map
   */
  updateCookiesVariationMap(variationMap) {
    const options = this.getCookieOptions()

    Object.keys(variationMap).forEach(experimentId => {
      const cookieName = `vt_${experimentId}`,
        localValue = parseInt(this.cookies.get(cookieName, {path: '/'}))

      if (localValue !== variationMap[experimentId]) {
        this.cookies.set(cookieName, `${variationMap[experimentId]}`, options)
      }
    })

    // Usefull for unit tests
    return options
  }

  /**
   * Update persisted variation map in database if necessary
   */
  async updatePersistedVariationMap(variationMap) {
    if (this.isAuthenticated) {
      const newExperiments = [],
        error = (msg, e) => {
          console.error(msg, e)
          rollbar.error(msg, e)
        }

      let experimentData

      try {
        // Get the persisted user experiments, this is cached by Apollo
        experimentData = (await this.client.query({
          query: USER_EXPERIMENTS
        })).data
      } catch (e) {
        error('Error fetching user experiment data', e)

        return
      }

      // Identify what are the new experiments
      Object.keys(variationMap).forEach(experimentId => {
        if (!experimentData.getUserExperiments.find(experiment => experiment.experimentId === experimentId)) {
          newExperiments.push({
            experimentId,
            variationId: `${variationMap[experimentId]}`
          })
        }
      })

      // Persist new experiments
      if (newExperiments.length > 0) {
        let result

        try {
          result = await this.client.mutate({
            mutation: SAVE_USER_EXPERIMENTS,
            variables: {experiments: newExperiments}
          })
        } catch (e) {
          error('Error saving user experiment data', e)

          return
        }

        // Update apollo cache with new user experiments
        experimentData.getUserExperiments.push(...result.data.saveUserExperiments)
        this.client.writeQuery({
          query: USER_EXPERIMENTS,
          data: experimentData
        })
      }
    }
  }

  /**
   * Get the forced variation map from url params.
   * Ex.: http://www.credible.com/?vt_8490985444=0&vt_8999489308=2
   */
  getForcedVariationMap() {
    const variationMap = {}

    this.config.forEach(experiment => {
      if (isExperimentEnabled(experiment)) {
        const variation = this.queryParams[`vt_${experiment.id}`]

        if (variation) {
          variationMap[experiment.id] = parseInt(variation)
        }
      }
    })

    return variationMap
  }

  /**
   * The default variation map is used when the experiments are disabled.
   * A disabled experiment will display the variation with id '0' or the one referenced in
   * the 'disabledDefaultVariationId' parameter
   */
  getDefaultVariationMap() {
    const variationMap = {}

    this.config.forEach(experiment => {
      variationMap[experiment.id] = experiment.disabledDefaultVariationId || 0
    })

    return variationMap
  }

  /**
   * The system should be disabled if the parameter vt_disabled is set in the url
   * Ex.: http://www.credible.com/?vt_disabled=1
   */
  updateDisabledFlag() {
    const options = this.getCookieOptions()

    if (Object.keys(this.queryParams).includes(DISABLED_PARAM)) {
      if (!this.queryParams[DISABLED_PARAM] || ['false', '0'].includes(this.queryParams[DISABLED_PARAM])) {
        this.cookies.set(DISABLED_PARAM, '', {
          ...options,
          expires: new Date(0)
        })
      } else {
        this.cookies.set(DISABLED_PARAM, 1, options)
      }
    }

    // Usefull for unit tests
    return options
  }

  /**
   * Check if the system is disable.
   */
  isDisabled() {
    return !!this.cookies.get(DISABLED_PARAM, {path: '/'})
  }

  /**
   * Find the experiment config by experiment id
   */
  getExperimenentConfig(id) {
    return this.config.find(experiment => experiment.id === id)
  }

  /**
   * Return an array of experiment ids that should be
   * activated automatically in the current url
   */
  getUrlExperimentsIds() {
    const experiments = this.config.filter(experiment =>

      // Ignore mannual experiments
      !experiment.isManualTriggering

      // Find the experiments that matches the url
      && experiment.urlConditions
      && experiment.urlConditions.find(urlCond => {
        if (urlCond.type === 'regexp') {
          const reg = new RegExp(urlCond.url),
            fullUrl = `${this.url}${queryString.stringify(this.queryParams)}`

          return reg.test(fullUrl)
        }

        return urlCond.url === this.url
      })
    )

    // Return an array of experiment ids
    return experiments.map(experiment => experiment.id)
  }

  setGaCustomDimensions(activeExperiments) {
    if (!isClient()) {
      return
    }

    activeExperiments.forEach(id => {
      const experiment = this.getExperimenentConfig(id),
        variation = experiment.variations.find(variation => variation.id === this.variationMap[id])

      if (experiment.gaCustomDimension) {
        set(`dimension${experiment.gaCustomDimension}`, variation.description)
      }
    })
  }
}

export {ExperimentService}

export default ExperimentService
